import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import {
  getMovimentacoesAtivo,
  createMovimentacaoAtivo,
  updateMovimentacaoAtivo,
  deleteMovimentacaoAtivo,
} from '../../../services/carteiraDiariaService';
import { MovimentacaoAtivo, TipoMovimentacao } from '../../../types/carteiraDiaria';
import { formatBRL, formatDate } from '../../../utils/formatters';

const TIPO_LABELS: Record<TipoMovimentacao, string> = {
  APORTE: 'Aporte',
  RESGATE: 'Resgate',
  AMORTIZACAO: 'Amortização',
  PAGAMENTO_JUROS: 'Pagamento de Juros',
};

const thSx = {
  backgroundColor: '#EEF2F7',
  color: '#4D5766',
  fontWeight: 600,
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  borderBottom: '2px solid #CFD8E5',
};

const tdSx = {
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  color: '#2D3748',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
};

const TipoSelect: React.FC<{ value: TipoMovimentacao; onChange: (v: TipoMovimentacao) => void }> = ({ value, onChange }) => (
  <Select
    size="small"
    value={value}
    onChange={e => onChange(e.target.value as TipoMovimentacao)}
    sx={{ fontSize: '12px', minWidth: 160 }}
  >
    <MenuItem value="APORTE">Aporte</MenuItem>
    <MenuItem value="RESGATE">Resgate</MenuItem>
    <MenuItem value="AMORTIZACAO">Amortização</MenuItem>
    <MenuItem value="PAGAMENTO_JUROS">Pagamento de Juros</MenuItem>
  </Select>
);

export interface HistoricoMovimentacoesSectionProps {
  fundoId: number;
  codigoAtivo: string;
  nomeAtivo: string;
  adm: string;
}

const HistoricoMovimentacoesSection: React.FC<HistoricoMovimentacoesSectionProps> = ({
  fundoId, codigoAtivo, nomeAtivo, adm,
}) => {
  const [movimentacoes, setMovimentacoes] = useState<MovimentacaoAtivo[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<{ data: string; tipo: TipoMovimentacao; valor: string }>({
    data: '', tipo: 'APORTE', valor: '',
  });
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [form, setForm] = useState<{ data: string; tipo: TipoMovimentacao; valor: string }>({
    data: '', tipo: 'APORTE', valor: '',
  });

  useEffect(() => {
    setLoading(true);
    getMovimentacoesAtivo(fundoId, codigoAtivo, nomeAtivo, adm)
      .then(setMovimentacoes)
      .catch(() => setMovimentacoes([]))
      .finally(() => setLoading(false));
  }, [fundoId, codigoAtivo, nomeAtivo, adm]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.data || !form.valor) return;
    setSaving(true);
    try {
      const nova = await createMovimentacaoAtivo({
        fundo: fundoId,
        codigo_ativo: codigoAtivo,
        nome_ativo: nomeAtivo,
        tipo: form.tipo,
        data: form.data,
        valor: parseFloat(form.valor),
      });
      setMovimentacoes(prev => [...prev, nova].sort((a, b) => a.data.localeCompare(b.data)));
      setForm({ data: '', tipo: 'APORTE', valor: '' });
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (mov: MovimentacaoAtivo) => {
    setEditingId(mov.id);
    setEditForm({ data: mov.data, tipo: mov.tipo, valor: String(parseFloat(mov.valor)) });
  };

  const cancelEdit = () => setEditingId(null);

  const handleSaveEdit = async (id: number) => {
    if (!editForm.data || !editForm.valor) return;
    setSaving(true);
    try {
      const updated = await updateMovimentacaoAtivo(id, {
        tipo: editForm.tipo,
        data: editForm.data,
        valor: parseFloat(editForm.valor),
      });
      setMovimentacoes(prev =>
        prev.map(m => m.id === id ? updated : m).sort((a, b) => a.data.localeCompare(b.data))
      );
      setEditingId(null);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Excluir esta movimentação?')) return;
    setDeletingId(id);
    try {
      await deleteMovimentacaoAtivo(id);
      setMovimentacoes(prev => prev.filter(m => m.id !== id));
    } finally {
      setDeletingId(null);
    }
  };

  const totalValor = movimentacoes.reduce((acc, mov) => acc + mov.valor_display, 0);

  return (
    <Box sx={{ p: 2, backgroundColor: '#F4F7F9', borderRadius: 1 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#4D5766' }}>
        Registrar Movimentação
      </Typography>
      <Box component="form" onSubmit={handleSubmit}>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
          <TextField
            label="Data" type="date" size="small" value={form.data}
            onChange={e => setForm(prev => ({ ...prev, data: e.target.value }))}
            InputLabelProps={{ shrink: true }} required sx={{ width: 160 }}
          />
          <FormControl size="small" sx={{ width: 200 }} required>
            <InputLabel>Tipo</InputLabel>
            <Select label="Tipo" value={form.tipo}
              onChange={e => setForm(prev => ({ ...prev, tipo: e.target.value as TipoMovimentacao }))}
            >
              <MenuItem value="APORTE">Aporte</MenuItem>
              <MenuItem value="RESGATE">Resgate</MenuItem>
              <MenuItem value="AMORTIZACAO">Amortização</MenuItem>
              <MenuItem value="PAGAMENTO_JUROS">Pagamento de Juros</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Valor (R$)" type="number" size="small" value={form.valor}
            onChange={e => setForm(prev => ({ ...prev, valor: e.target.value }))}
            inputProps={{ min: 0, step: '0.01' }} required sx={{ width: 160 }}
          />
          <Button type="submit" variant="contained" size="small" disabled={saving}
            sx={{ height: 40, whiteSpace: 'nowrap' }}
          >
            {saving ? <CircularProgress size={16} /> : 'Salvar'}
          </Button>
        </Stack>
        {(form.tipo === 'AMORTIZACAO' || form.tipo === 'PAGAMENTO_JUROS') && (
          <Typography sx={{ fontSize: '11px', color: '#8A98A8', mt: 0.75 }}>
            Valor total recebido pelo fundo (R$/cota × quantidade de cotas do dia)
          </Typography>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#4D5766' }}>
        Histórico de Movimentações
      </Typography>
      {loading ? (
        <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={20} />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #CFD8E5', borderRadius: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={thSx}>Data</TableCell>
                <TableCell sx={thSx}>Tipo</TableCell>
                <TableCell sx={{ ...thSx, textAlign: 'right' }}>Valor</TableCell>
                <TableCell sx={{ ...thSx, width: 80 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {movimentacoes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 2, color: '#8A98A8', fontSize: '12px' }}>
                    Nenhuma movimentação registrada.
                  </TableCell>
                </TableRow>
              ) : (
                movimentacoes.map(mov => {
                  const isEditing = editingId === mov.id;
                  const isDeleting = deletingId === mov.id;
                  return (
                    <TableRow key={mov.id} sx={{ '&:nth-of-type(even)': { backgroundColor: '#F9FAFB' } }}>
                      {isEditing ? (
                        <>
                          <TableCell sx={tdSx}>
                            <TextField type="date" size="small" value={editForm.data}
                              onChange={e => setEditForm(prev => ({ ...prev, data: e.target.value }))}
                              InputLabelProps={{ shrink: true }} sx={{ fontSize: '12px', width: 140 }}
                            />
                          </TableCell>
                          <TableCell sx={tdSx}>
                            <TipoSelect value={editForm.tipo}
                              onChange={v => setEditForm(prev => ({ ...prev, tipo: v }))}
                            />
                          </TableCell>
                          <TableCell sx={tdSx}>
                            <TextField type="number" size="small" value={editForm.valor}
                              onChange={e => setEditForm(prev => ({ ...prev, valor: e.target.value }))}
                              inputProps={{ min: 0, step: '0.01' }} sx={{ fontSize: '12px', width: 130 }}
                            />
                          </TableCell>
                          <TableCell sx={{ ...tdSx, overflow: 'visible', whiteSpace: 'nowrap' }}>
                            <Tooltip title="Salvar">
                              <IconButton size="small" onClick={() => handleSaveEdit(mov.id)} disabled={saving} sx={{ color: '#29A152' }}>
                                {saving ? <CircularProgress size={14} /> : <CheckIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancelar">
                              <IconButton size="small" onClick={cancelEdit} sx={{ color: '#E51739' }}>
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell sx={tdSx}>{formatDate(mov.data)}</TableCell>
                          <TableCell sx={tdSx}>{TIPO_LABELS[mov.tipo]}</TableCell>
                          <TableCell sx={{ ...tdSx, textAlign: 'right',
                            color: mov.valor_display < 0 ? '#E51739' : '#29A152', fontWeight: 500 }}
                          >
                            {formatBRL(mov.valor_display)}
                          </TableCell>
                          <TableCell sx={{ ...tdSx, overflow: 'visible', whiteSpace: 'nowrap' }}>
                            <Tooltip title="Editar">
                              <EditIcon onClick={() => startEdit(mov)} style={{ cursor: 'pointer', color: '#2C91AD', marginRight: '8px' }} />
                            </Tooltip>
                            <Tooltip title="Remover">
                              {isDeleting
                                ? <CircularProgress size={14} />
                                : <DeleteIcon onClick={() => handleDelete(mov.id)} style={{ cursor: 'pointer', color: '#E51739' }} />
                              }
                            </Tooltip>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  );
                })
              )}
              <TableRow sx={{ backgroundColor: '#E8EEF4', borderTop: '2px solid #CFD8E5' }}>
                <TableCell colSpan={2} sx={{ ...tdSx, fontWeight: 700, color: '#2C3E50' }}>
                  Valor Financiado Atualizado
                </TableCell>
                <TableCell sx={{ ...tdSx, textAlign: 'right', fontWeight: 700,
                  color: totalValor < 0 ? '#E51739' : '#2C91AD' }}
                >
                  {formatBRL(totalValor)}
                </TableCell>
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default HistoricoMovimentacoesSection;
